// src/pages/admin/AdminAdmins.js
// Gestion des administrateurs (SUPER_ADMIN uniquement) - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ROUTES from '../../constants/routes';
import { ADMIN_ROLES } from '../../constants/roles';
import { getRoleBadge, canDeleteAdmin, canModifyAdmin, getAssignableRoles } from '../../permissions/checkPermission';

const AdminAdmins = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', role: ADMIN_ROLES.SUPPORT });

  // Simuler le rôle de l'admin connecté (à remplacer par useAuth)
  const currentAdminRole = ADMIN_ROLES.SUPER_ADMIN;

  useEffect(() => {
    const demoAdmins = [
      { id: 1, name: 'Yvan Karel', email: 'yvan@fynho.com', role: ADMIN_ROLES.SUPER_ADMIN, createdAt: '2026-01-01' },
      { id: 2, name: 'Admin Support', email: 'support@fynho.com', role: ADMIN_ROLES.SUPPORT, createdAt: '2026-03-15' },
      { id: 3, name: 'Éditeur Contenu', email: 'editor@fynho.com', role: ADMIN_ROLES.EDITOR, createdAt: '2026-04-10' },
    ];
    setAdmins(demoAdmins);
  }, []);

  const handleAdd = () => {
    if (!newAdmin.name.trim() || !newAdmin.email.trim()) {
      alert('Nom et email requis');
      return;
    }
    // TODO: adminAdminsAPI.create(newAdmin)
    const admin = { id: Date.now(), ...newAdmin, createdAt: new Date().toISOString().split('T')[0] };
    setAdmins((prev) => [...prev, admin]);
    setNewAdmin({ name: '', email: '', role: ADMIN_ROLES.SUPPORT });
    setShowAddForm(false);
  };

  const handleRoleChange = (adminId, adminRole, newRole) => {
    if (!canModifyAdmin(currentAdminRole, adminRole)) {
      alert('Vous ne pouvez pas modifier cet administrateur');
      return;
    }
    // TODO: adminAdminsAPI.update(adminId, { role: newRole })
    setAdmins((prev) => prev.map((a) => (a.id === adminId ? { ...a, role: newRole } : a)));
  };

  const handleDelete = (adminId, adminRole) => {
    if (!canDeleteAdmin(currentAdminRole, adminRole)) {
      alert('Vous ne pouvez pas supprimer cet administrateur');
      return;
    }
    if (window.confirm('Supprimer définitivement cet administrateur ?')) {
      // TODO: adminAdminsAPI.delete(adminId)
      setAdmins((prev) => prev.filter((a) => a.id !== adminId));
    }
  };

  const assignableRoles = getAssignableRoles(currentAdminRole);

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', maxWidth: '900px', margin: '0 auto', backgroundColor: '#0A0E1A' },
    header: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', position: 'sticky', top: 0, backgroundColor: '#0A0E1A', zIndex: 50 },
    backButton: { background: 'none', border: 'none', color: '#FFFFFF', fontSize: '20px', cursor: 'pointer' },
    title: { fontSize: '20px', fontWeight: '700', color: '#FFFFFF', flex: 1 },
    addButton: { padding: '8px 16px', backgroundColor: '#FFD700', border: 'none', borderRadius: '8px', color: '#0A0E1A', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
    addForm: { margin: '16px 20px', padding: '16px', backgroundColor: '#162033', borderRadius: '12px', border: '1px solid rgba(255, 215, 0, 0.3)', display: 'flex', flexDirection: 'column', gap: '12px' },
    field: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '12px', fontWeight: '600', color: '#A0AEC0' },
    input: { padding: '10px 12px', backgroundColor: '#111827', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#FFFFFF', fontSize: '14px', outline: 'none', width: '100%' },
    select: { padding: '10px 12px', backgroundColor: '#111827', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#FFFFFF', fontSize: '14px', outline: 'none', width: '100%' },
    formButtons: { display: 'flex', gap: '10px' },
    saveBtn: { flex: 1, padding: '10px', backgroundColor: '#FFD700', border: 'none', borderRadius: '8px', color: '#0A0E1A', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
    cancelBtn: { flex: 1, padding: '10px', backgroundColor: '#162033', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#A0AEC0', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    list: { display: 'flex', flexDirection: 'column', gap: '12px', padding: '0 20px 40px' },
    card: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', backgroundColor: '#162033', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.1)' },
    avatar: { width: '48px', height: '48px', borderRadius: '50%', backgroundColor: 'rgba(255, 215, 0, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 },
    body: { flex: 1 },
    name: { fontSize: '15px', fontWeight: '600', color: '#FFFFFF' },
    email: { fontSize: '13px', color: '#A0AEC0', marginTop: '2px' },
    roleBadge: { display: 'inline-block', marginTop: '6px', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700' },
    actions: { display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' },
    roleSelect: { padding: '6px 8px', backgroundColor: '#111827', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '6px', color: '#FFFFFF', fontSize: '11px', outline: 'none' },
    deleteBtn: { padding: '5px 10px', backgroundColor: 'rgba(245, 101, 101, 0.15)', color: '#F56565', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Administrateurs | Fynho Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}>←</button>
        <div style={styles.title}>Administrateurs</div>
        <button style={styles.addButton} onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? '✕' : '+ Ajouter'}
        </button>
      </div>

      {showAddForm && (
        <div style={styles.addForm}>
          <div style={styles.field}>
            <label style={styles.label}>Nom</label>
            <input type="text" style={styles.input} value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} placeholder="Nom complet" />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input type="email" style={styles.input} value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} placeholder="admin@fynho.com" />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Rôle</label>
            <select style={styles.select} value={newAdmin.role} onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}>
              {assignableRoles.map((role) => (
                <option key={role} value={role}>{getRoleBadge(role).label}</option>
              ))}
            </select>
          </div>
          <div style={styles.formButtons}>
            <button style={styles.cancelBtn} onClick={() => setShowAddForm(false)}>Annuler</button>
            <button style={styles.saveBtn} onClick={handleAdd}>Créer</button>
          </div>
        </div>
      )}

      <div style={styles.list}>
        {admins.map((admin) => {
          const badge = getRoleBadge(admin.role);
          const canModify = canModifyAdmin(currentAdminRole, admin.role);
          return (
            <div key={admin.id} style={styles.card}>
              <div style={styles.avatar}>🔑</div>
              <div style={styles.body}>
                <div style={styles.name}>{admin.name}</div>
                <div style={styles.email}>{admin.email}</div>
                <div style={{ ...styles.roleBadge, backgroundColor: `${badge.color}22`, color: badge.color }}>
                  {badge.label}
                </div>
              </div>
              {canModify && (
                <div style={styles.actions}>
                  <select
                    style={styles.roleSelect}
                    value={admin.role}
                    onChange={(e) => handleRoleChange(admin.id, admin.role, e.target.value)}
                  >
                    {assignableRoles.map((role) => (
                      <option key={role} value={role}>{getRoleBadge(role).label}</option>
                    ))}
                  </select>
                  <button style={styles.deleteBtn} onClick={() => handleDelete(admin.id, admin.role)}>
                    Suppr.
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminAdmins;